import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import { idempotencyKeyFor } from '@/lib/http-core';
import type { QuantityDiscountConfig } from './types';

/**
 * ID49 전역 수량할인 정책 GET/PUT (per-product 아님 — 기본설정>상품정책>상품 수량별 할인 설정).
 * BE 계약(be-catalog): `/admin/v1/quantity-discount`.
 * generated OpenAPI 미반영 → adminFetch 로 직접 호출(카테고리 훅 선례).
 * PUT 은 upsert(전체 교체). 미설정 시 GET 이 200+data=null(또는 404) → 빈 폼.
 */
interface ConfigEnvelope {
  success: boolean;
  data?: QuantityDiscountConfig | null;
}

const PATH = '/admin/v1/quantity-discount';

const quantityDiscountKey = () => ['admin', 'quantity-discount'] as const;

export const useQuantityDiscountQuery = () =>
  useQuery<QuantityDiscountConfig | null>({
    queryKey: quantityDiscountKey(),
    queryFn: async () => {
      const res = await adminFetch(PATH);
      if (res.status === 404) return null; // 미설정 → 빈 폼
      if (!res.ok) throw new Error(`quantity-discount fetch failed: ${res.status}`);
      const env = (await res.json()) as ConfigEnvelope;
      return env.data ?? null;
    },
  });

export const useSaveQuantityDiscountMutation = () => {
  const qc = useQueryClient();
  return useMutation<QuantityDiscountConfig, Error, QuantityDiscountConfig>({
    mutationFn: async (config) => {
      const res = await adminFetch(PATH, {
        method: 'PUT',
        headers: {
          'Idempotency-Key': idempotencyKeyFor('PUT', PATH, config),
        },
        body: JSON.stringify(config),
      });
      if (!res.ok) throw new Error(`quantity-discount save failed: ${res.status}`);
      const env = (await res.json()) as ConfigEnvelope;
      return env.data ?? config;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: quantityDiscountKey() }),
  });
};
